import React from 'react';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Search, FilterAlt, Xmark } from 'iconoir-react';
import { TIMELINE_CATEGORIES } from '../../constants/timelineCategories';

// Filter bar above the timeline. Pure controlled component — owner of the
// filter state lives one level up so it can be persisted to localStorage.
export function TimelineFilters({
  filters, onChange, allTags, totalCount, visibleCount,
}) {
  const toggleCategory = (value) => {
    const next = filters.categories.includes(value)
      ? filters.categories.filter((c) => c !== value)
      : [...filters.categories, value];
    onChange({ ...filters, categories: next });
  };

  const toggleTeam = (name) => {
    const next = filters.teams.includes(name)
      ? filters.teams.filter((t) => t !== name)
      : [...filters.teams, name];
    onChange({ ...filters, teams: next });
  };

  const setOwner = (name) => {
    onChange({ ...filters, owner: filters.owner === name ? '' : name });
  };

  const clear = () => {
    onChange({ search: '', categories: [], owner: '', teams: [], quickChip: '' });
  };

  const setQuickChip = (chip) => {
    onChange({ ...filters, quickChip: filters.quickChip === chip ? '' : chip });
  };

  const hasActive = !!(
    filters.search || filters.categories.length || filters.owner ||
    filters.teams.length || filters.quickChip
  );

  // Tags grouped by kind for the team multi-select.
  const tagsByKind = {
    role: allTags.filter((t) => t.kind === 'role'),
    vendor: allTags.filter((t) => t.kind === 'vendor'),
    custom: allTags.filter((t) => t.kind === 'custom'),
  };

  return (
    <Card className="card-surface p-4 space-y-3" data-testid="timeline-filters">
      {/* Search + count + clear */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-muted absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" strokeWidth={1.5} />
          <Input
            type="text"
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
            placeholder="Search timeline items, descriptions, locations..."
            className="input-field h-10 pl-9"
            data-testid="timeline-search-input"
          />
        </div>
        <div className="flex items-center gap-2 text-xs text-muted">
          <FilterAlt className="w-3.5 h-3.5" strokeWidth={1.5} />
          <span data-testid="timeline-filter-count">
            {visibleCount} of {totalCount} item{totalCount === 1 ? '' : 's'}
          </span>
          {hasActive && (
            <button
              onClick={clear}
              className="text-terracotta hover:underline ml-1 inline-flex items-center gap-1"
              data-testid="clear-filters-btn"
            >
              <Xmark className="w-3 h-3" /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Quick chips */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[11px] uppercase tracking-wider text-muted">Quick:</span>
        {[
          { value: 'bride', label: 'Bride' },
          { value: 'groom', label: 'Groom' },
          { value: 'planner', label: 'Planner only' },
          { value: 'photo_video', label: 'Photo & video' },
        ].map((c) => (
          <button
            key={c.value}
            onClick={() => setQuickChip(c.value)}
            className={`px-2.5 py-1 rounded-full text-xs font-light tracking-wide border transition-colors ${
              filters.quickChip === c.value
                ? 'bg-charcoal text-white border-charcoal'
                : 'bg-white text-charcoal border-stone hover:border-charcoal'
            }`}
            data-testid={`quick-filter-${c.value}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Category chips */}
      <div className="flex flex-wrap gap-1.5">
        <span className="text-[11px] uppercase tracking-wider text-muted self-center mr-1">Categories:</span>
        {TIMELINE_CATEGORIES.map((cat) => {
          const Icon = cat.icon;
          const active = filters.categories.includes(cat.value);
          return (
            <button
              key={cat.value}
              onClick={() => toggleCategory(cat.value)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-all ${
                active ? 'text-white' : 'text-charcoal hover:border-charcoal/50'
              }`}
              style={{
                backgroundColor: active ? cat.color : `${cat.color}10`,
                borderColor: active ? cat.color : `${cat.color}40`,
              }}
              data-testid={`category-filter-${cat.value}`}
            >
              <Icon className="w-3 h-3" strokeWidth={1.5} />
              <span>{cat.label}</span>
            </button>
          );
        })}
      </div>

      {/* Owner / team filter row */}
      <div className="flex flex-col md:flex-row gap-3">
        {/* Primary owner select */}
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-muted mb-1">Primary owner</p>
          <select
            value={filters.owner || ''}
            onChange={(e) => setOwner(e.target.value)}
            className="w-full h-9 px-3 rounded-sm border border-border bg-white text-sm text-charcoal"
            data-testid="owner-filter-select"
          >
            <option value="">Any owner</option>
            {tagsByKind.role.length > 0 && (
              <optgroup label="Wedding roles">
                {tagsByKind.role.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </optgroup>
            )}
            {tagsByKind.vendor.length > 0 && (
              <optgroup label="Vendor teams">
                {tagsByKind.vendor.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </optgroup>
            )}
            {tagsByKind.custom.length > 0 && (
              <optgroup label="Custom">
                {tagsByKind.custom.map((t) => (
                  <option key={t.id} value={t.name}>{t.name}</option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        {/* Supporting teams chips */}
        <div className="flex-[2] min-w-0">
          <p className="text-[11px] uppercase tracking-wider text-muted mb-1">Supporting teams (any of)</p>
          <div className="flex flex-wrap gap-1 max-h-20 overflow-y-auto pr-1">
            {allTags.length === 0 ? (
              <span className="text-xs text-muted italic">No tags yet — defaults will appear here.</span>
            ) : (
              allTags.map((t) => {
                const active = filters.teams.includes(t.name);
                return (
                  <button
                    key={t.id}
                    onClick={() => toggleTeam(t.name)}
                    className={`px-2 py-0.5 rounded-full text-[11px] border transition-colors ${
                      active
                        ? 'bg-moss text-white border-moss'
                        : 'bg-white text-charcoal border-stone hover:border-charcoal'
                    }`}
                    data-testid={`team-filter-${t.id}`}
                  >
                    {t.name}
                  </button>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Active filter pills (compact summary) */}
      {hasActive && (
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-border">
          {filters.quickChip && (
            <Badge className="bg-charcoal text-white border-charcoal text-[10px] uppercase">
              {filters.quickChip.replace('_', ' ')}
            </Badge>
          )}
          {filters.categories.map((c) => (
            <Badge key={c} className="bg-terracotta/10 text-terracotta border-terracotta/30 border text-[10px] uppercase">
              {TIMELINE_CATEGORIES.find((x) => x.value === c)?.label || c}
            </Badge>
          ))}
          {filters.owner && (
            <Badge className="bg-ochre/10 text-ochre border-ochre/30 border text-[10px] uppercase">
              Owner: {filters.owner}
            </Badge>
          )}
          {filters.teams.map((t) => (
            <Badge key={t} className="bg-moss/10 text-moss border-moss/30 border text-[10px] uppercase">
              {t}
            </Badge>
          ))}
        </div>
      )}
    </Card>
  );
}
